/**
 * What was scanned, and what can be done with it.
 *
 * The person reading this is standing in front of a rack of near-identical
 * steel with a tool in their hands. The first job of this card is to let them
 * confirm they picked up the right one - which an asset code alone cannot do, so
 * the name, project and owner come first and the spec follows.
 *
 * The second job is custody, and it is the reason the ordering is what it is.
 * "Held by R. Patil since Tuesday" has to be visible *before* someone checks the
 * tool out again, because that is the moment the system can still prevent two
 * people believing they have it.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, PackageX, UserRound } from 'lucide-react';
import { Badge, Card, CardBody, Code, type BadgeTone } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { hasOwnName, type MouldRow } from '../../db/queries.js';

const STATUS_TONE: Record<string, BadgeTone> = {
  IN_STORE: 'success',
  ISSUED: 'primary',
  IN_TRANSIT: 'warning',
  AT_VENDOR: 'warning',
  UNDER_MAINTENANCE: 'warning',
  SCRAPPED: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  IN_STORE: 'In store',
  ISSUED: 'Checked out',
  IN_TRANSIT: 'In transit',
  AT_VENDOR: 'At vendor',
  UNDER_MAINTENANCE: 'Maintenance',
  SCRAPPED: 'Scrapped',
};

export function statusLabel(status: string | null | undefined): string {
  return status ? (STATUS_LABEL[status] ?? status) : 'Unknown';
}

export function statusTone(status: string | null | undefined): BadgeTone {
  return status ? (STATUS_TONE[status] ?? 'neutral') : 'neutral';
}

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}

/** One spec line. Absent values are dropped rather than shown as a dash. */
function Spec({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm">{value}</dd>
    </div>
  );
}

export interface ScanResultProps {
  code: string;
  mould: MouldRow | null | undefined;
  /** Who holds it, by name - resolved online; the device holds only an id. */
  heldByName?: string | null;
  children?: React.ReactNode;
}

export function ScanResult({ code, mould, heldByName, children }: ScanResultProps) {
  // A plate's or label's QR carries the mould's internal id - never shown as if it were a code.
  const isId = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|c[0-9a-z]{24})$/i.test(code.trim());
  // `undefined` is "still looking"; `null` is "looked, and it is not here".
  if (mould === undefined) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-muted-foreground">Looking up {isId ? 'the mould' : code}…</p>
        </CardBody>
      </Card>
    );
  }

  if (mould === null) {
    return (
      <Card>
        <CardBody className="text-center">
          <PackageX className="mx-auto mb-2 size-8 text-muted-foreground" aria-hidden />
          <p className="font-medium">
            {isId ? 'No mould on this device matches this QR code' : <>Nothing on this device matches <Code>{code}</Code></>}
          </p>
          {/* Two very different causes, and the person on the floor can act on
              both - so both are named rather than one generic failure. */}
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Either the code is not a mould in this system, or this device has not
            finished downloading the catalog yet. Check the code, or open Your
            account and sync.
          </p>
        </CardBody>
      </Card>
    );
  }

  const held = heldByName ?? (mould.issuedToUserId ? 'someone' : null);
  const overdue =
    mould.expectedBackBy && new Date(mould.expectedBackBy).getTime() < Date.now();
  const scrapped = mould.status === 'SCRAPPED' || mould.isActive === false;

  return (
    <Card>
      <CardBody>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Code className="text-base font-semibold">{mould.assetCode}</Code>
              <Badge tone={statusTone(mould.status)}>{statusLabel(mould.status)}</Badge>
              {mould._pending ? <Badge tone="warning">Not yet synced</Badge> : null}
            </div>
            {hasOwnName(mould) ? (
              <p className="mt-1 truncate text-lg font-medium">{mould.name}</p>
            ) : null}
            <p className="mt-0.5 text-sm text-muted-foreground">
              {[mould.projectName, mould.ownerCompanyName].filter(Boolean).join(' · ') ||
                'No project or owner recorded'}
            </p>
          </div>
          <Button variant="secondary" size="sm" asChild>
            <Link to={`/moulds/${encodeURIComponent(mould.assetCode)}`}>
              Full record <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>

        {/* Custody, before the actions. Somebody is about to decide whether to
            take this tool, and this is the line that should stop them. */}
        {held ? (
          <div
            className={
              overdue
                ? 'mb-3 flex items-start gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger'
                : 'mb-3 flex items-start gap-2 rounded-lg bg-surface-sunken px-3 py-2 text-sm'
            }
          >
            <UserRound className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              Held by <strong>{held}</strong>
              {mould.issuedAt ? ` since ${when(mould.issuedAt)}` : ''}
              {mould.expectedBackBy
                ? overdue
                  ? ` · due back ${new Date(mould.expectedBackBy).toLocaleDateString()}, overdue`
                  : ` · due back ${new Date(mould.expectedBackBy).toLocaleDateString()}`
                : ''}
            </span>
          </div>
        ) : null}

        {scrapped ? (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              This mould is scrapped or retired. The server will refuse a scan against
              it - if that is wrong, an administrator can reactivate it on the full record.
            </span>
          </div>
        ) : null}

        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Spec label="Tool type" value={mould.toolTypeName} />
          <Spec label="Cavities" value={mould.cavities} />
          <Spec label="Mould size" value={mould.moldSize} />
          <Spec label="Material" value={mould.materialGradeName} />
          <Spec label="Gate" value={mould.gateTypeName} />
          <Spec label="Runner" value={mould.runnerTypeName} />
          <Spec label="Made by" value={mould.manufactureCompanyName} />
          <Spec label="Year" value={mould.manufactureYear} />
        </dl>

        {children ? <div className="mt-4 border-t border-border pt-4">{children}</div> : null}
      </CardBody>
    </Card>
  );
}
